
import { isTweet } from '@@/bits/post_attachment'
import { PostColor } from '@padlet/arvo'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { isAttachmentBlocked } from 'bits/attachments'
import device from 'bits/device'
import { Component, defineAsyncComponent, Getter, namespace, Prop, Vue, Watch } from 'bits/vue'
import { isAttachmentSupportedByZoom } from 'bits/zoom'
import OzIndeterminateSpinner from 'library/v4/components/OzIndeterminateSpinner.vue'
import type { Post } from 'types'

enum ResizableVideoProvider {
  DAILYMOTION = 'dailymotion',
  LOOM = 'loom',
  VIMEO = 'vimeo',
  YOUTUBE = 'youtube',
}

@Component({
  components: {
    OzIndeterminateSpinner,
    SurfaceAttachmentContentBlocked: defineAsyncComponent(
      () => import('vuecomponents/SurfaceAttachmentContentBlocked.vue'),
    ),
    SurfacePanelPostExpandedAttachmentFile: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentFile.vue'),
    ),
    SurfacePanelPostExpandedAttachmentImage: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentImage.vue'),
    ),
    SurfacePanelPostExpandedAttachmentAudio: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentAudio.vue'),
    ),
    SurfacePanelPostExpandedAttachmentVideo: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentVideo.vue'),
    ),
    SurfacePanelPostExpandedAttachmentEmbed: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentEmbed.vue'),
    ),
    SurfacePanelPostExpandedAttachmentLink: defineAsyncComponent(
      () => import('vuecomponents/SurfacePanelPostExpandedAttachmentLink.vue'),
    ),
  },
})
export default class SurfacePanelPostExpandedAttachment extends Vue {
  $refs!: {
    attachmentArea: HTMLDivElement
    wrapper: HTMLDivElement
  }

  @Prop({ required: true }) darkMode!: true | false | 'auto'
  @Prop({ required: true }) displayAttributes!: BeethovenDisplayAttributes | null
  /**
   * Safe area is an area in the top left corner of the attachment area
   * that the attachment can't be displayed on top of.
   */
  @Prop() safeArea?: { width: number; height: number }

  @Getter blockedContentProviders!: Set<string>
  @namespace('browser').Getter isSmallerThanDesktop!: boolean
  @namespace('browser').Getter isSmallerThanTabletPortrait!: boolean
  @namespace('postAction').Getter('expandedPost') post!: Post

  isLoaded = false
  device = device

  get lowerCaseProviderName(): string | undefined {
    return this.displayAttributes?.provider_name.toLowerCase()
  }

  get url(): string {
    return this.post.wish_content?.attachment_props.url || this.post.attachment
  }

  get hasBlockedContent(): boolean {
    if (!this.displayAttributes) return false
    return isAttachmentBlocked(this.displayAttributes, this.blockedContentProviders)
  }

  get isFile(): boolean {
    return this.displayAttributes?.content_category === 'file'
  }

  get isImage(): boolean {
    return this.displayAttributes?.content_category === 'photo'
  }

  get isEmbedCodeImgTag(): boolean {
    if (!this.displayAttributes?.embed_code) return false
    const $embed = document.createElement('div')
    $embed.innerHTML = this.displayAttributes?.embed_code
    const embedTag = $embed.firstChild as Element
    return embedTag.tagName.toLowerCase() === 'img'
  }

  get isAudio(): boolean {
    return this.displayAttributes?.content_category === 'audio'
  }

  get isVideo(): boolean {
    return this.displayAttributes?.content_category === 'video'
  }

  get isResizableVideo(): boolean {
    return Object.values(ResizableVideoProvider).includes(this.lowerCaseProviderName as ResizableVideoProvider)
  }

  get isYoutube(): boolean {
    return this.lowerCaseProviderName === 'youtube'
  }

  get isSpotify(): boolean {
    return this.lowerCaseProviderName === 'spotify'
  }

  get isUpload(): boolean {
    return this.lowerCaseProviderName === 'padlet drive'
  }

  get isDocument(): boolean {
    return this.displayAttributes?.content_category === 'document'
  }

  get isPdf(): boolean {
    return (
      this.displayAttributes?.content_category === 'document' && this.displayAttributes?.content_subcategory === 'pdf'
    )
  }

  get isArticle(): boolean {
    return (
      this.displayAttributes?.content_category === 'page' &&
      (this.displayAttributes?.content_subcategory as string) === 'article'
    )
  }

  get isHtml(): boolean {
    return this.displayAttributes?.content_category === 'page' && this.displayAttributes?.content_subcategory === 'html'
  }

  get isPadletWall(): boolean {
    // provider_name for a wall is the builder's name, "padlet" should be used only for landside pages (e.g. padlet.com, padlet.com/features)
    return (
      this.displayAttributes?.content_subcategory === 'padlet' &&
      !!this.displayAttributes?.provider_name &&
      this.lowerCaseProviderName !== 'padlet'
    )
  }

  get isFacebookPost(): boolean {
    return this.displayAttributes?.content_category === 'page' && this.lowerCaseProviderName === 'facebook'
  }

  get isInstagramPost(): boolean {
    return this.displayAttributes?.content_category === 'page' && this.lowerCaseProviderName === 'instagram'
  }

  get isTweet(): boolean {
    return isTweet(this.post.wish_content?.attachment_props)
  }

  get postColor(): PostColor | null {
    return this.post.color
  }

  get isAttachmentScreenCenteredOnMobile(): boolean {
    if (!this.isSmallerThanTabletPortrait) return false
    return this.isFile || this.isImage || this.isAudio || this.isVideo || !this.displayAttributes?.embed_code
  }

  get attachmentAreaStyles(): object {
    if (this.isSmallerThanDesktop || !this.safeArea) return {}
    if (this.isResizableVideo) {
      return {
        paddingTop: `${this.safeArea.height}px`,
        paddingBottom: '16px',
      }
    }
    if (
      !(this.isImage || this.isEmbedCodeImgTag) &&
      !this.isAudio &&
      !this.isVideo &&
      !this.isFile &&
      this.displayAttributes?.embed_code
    ) {
      // Use the same condition that renders SurfacePanelPostExpandedAttachmentEmbed.
      // Attachments that fall into this category are usually scrollable (document
      // files, web pages, etc), so we don't pad the bottom.
      return {
        width: `calc(100% - ${this.safeArea.width}px)`,
      }
    }
    return this.attachmentAreaPaddings
  }

  attachmentAreaPaddings = {
    paddingLeft: '0px',
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
  }

  handleAttachmentResize({ width, height }: { width: number; height: number }): void {
    if (this.isSmallerThanDesktop || !this.safeArea) return

    const { clientWidth: maxWidth, clientHeight: maxHeight } = this.$refs.attachmentArea
    // Since attachment is centered in the attachment area, we can calculate its left
    // and top like this:
    const left = Math.floor((maxWidth - width) / 2)
    const top = Math.floor((maxHeight - height) / 2)

    if (left > this.safeArea.width || top > this.safeArea.height) {
      this.attachmentAreaPaddings = {
        paddingLeft: '0px',
        paddingTop: '0px',
        paddingRight: '0px',
        paddingBottom: '0px',
      }
      return
    }

    const horizontalDistanceToCloseBtn = this.safeArea.width - left
    const verticalDistanceToCloseBtn = this.safeArea.height - top

    if (horizontalDistanceToCloseBtn < verticalDistanceToCloseBtn) {
      this.attachmentAreaPaddings.paddingLeft = `${this.safeArea.width}px`
      this.attachmentAreaPaddings.paddingRight = '16px'
    } else if (horizontalDistanceToCloseBtn > verticalDistanceToCloseBtn) {
      this.attachmentAreaPaddings.paddingTop = `${this.safeArea.height}px`
      this.attachmentAreaPaddings.paddingBottom = '16px'
    }
  }

  get isUnsupportedZoomAttachment(): boolean {
    // Zoom doesn't allow playing of some media types, so we open in browser
    return (
      device.zoomApp &&
      isAttachmentSupportedByZoom({
        contentCategory: this.displayAttributes?.content_category,
        contentSubcategory: this.displayAttributes?.content_subcategory,
        providerName: this.displayAttributes?.provider_name,
      })
    )
  }

  handleAttachmentLoaded(): void {
    this.isLoaded = true
  }

  @Watch('displayAttributes')
  onDisplayAttributesChange(): void {
    if (this.isFile || (!this.isImage && !this.displayAttributes?.embed_code)) {
      this.isLoaded = true
    }
  }
}
